/**
 * The doc-level side of the raster pipeline, shared by generatePlan, rearrangeHabitats and carveAreas:
 * classifying the grid from a plan, building walking routes, writing traced rings into the shared corner pool,
 * and adding pools for zones that need water. Works on plain docs and immer drafts alike.
 */
import type { AnimalAreaKind, AreaId, PlanDoc, Pt, SpeciesId, VertId } from '../../shared/types';
import { pip, ringVerts, roadPoly, riverPoly, smoothRing } from '../geom';
import { isAnimalArea } from '../model';
import {
  absorbHoles, CLS_LAND, CLS_OUT, CLS_RESERVED, CLS_ROAD, CLS_WATER, cellMid, cornerToMetres, forCellsInPoly,
  forCellsNearLine, makeGrid, makeRoutes, nbrs4, packAlongRoutes, simplifyLoop, traceMask, traceRegions,
  type Grid, type Rng, type Route, type TraceResult,
} from '../raster';

/** Makes ids that aren't used yet in a record, deterministically (p0, p1, … in base 36). */
export type IdMaker = (rec: Record<string, unknown>, prefix: string) => string;

/** A deterministic id maker; ids already in the record are skipped. */
export function idMaker(): IdMaker {
  let k = 0;
  return (rec, p) => {
    let id: string;
    do id = p + (k++).toString(36); while (Object.hasOwn(rec, id));
    return id;
  };
}

/** Wall simplification tolerance in cells (the prototype's 1.3). */
export const TRACE_TOL = 1.3;
/** Extra margin (in cells) around a road surface that packing keeps clear. */
export const ROAD_MARGIN_CELLS = 0.6;
/** Radius (m) kept clear around a shop or service icon. */
export const ICON_CLEAR_M = 7;

/** The smoothed ring of an area, computed straight from the doc (no cache, safe on drafts). */
export function areaRing(doc: PlanDoc, id: AreaId): Pt[] {
  const a = doc.areas[id];
  return a ? smoothRing(ringVerts(doc, a.verts)) : [];
}

/** The smoothed zoo boundary, or null. */
export function plotRing(doc: PlanDoc): Pt[] | null {
  return doc.plot ? smoothRing(ringVerts(doc, doc.plot.verts), doc.plot.smooth) : null;
}

/** Road centre lines with half widths per segment, for stamping. */
function roadLines(doc: PlanDoc): { id: string; poly: Pt[]; hw: (s: number) => number }[] {
  return Object.entries(doc.roads).map(([id, road]) => {
    const { pts, widths } = roadPoly(doc, road);
    return { id, poly: pts, hw: (s: number) => Math.max(widths[s] ?? 0, widths[s + 1] ?? 0) / 2 };
  });
}

/** Cell classes, cells walls may not cut through (road surfaces, kept zones), and the kept zones' cells. */
export interface Classified { g: Grid; cls: Uint8Array; hard: Uint8Array; kept: Uint8Array }

/**
 * Classifies the grid from the plan: outside, land, road, water (lakes, rivers) and reserved (every other area
 * except pools and the zones in `filling`, plus a clear circle around each shop icon). Animal zones that are not
 * being filled (locked ones) are reserved with a one-cell buffer and marked hard, so new walls stay off them.
 */
export function classify(doc: PlanDoc, filling: ReadonlySet<AreaId>): Classified {
  const g = makeGrid(doc.size), cls = new Uint8Array(g.n), hard = new Uint8Array(g.n), kept = new Uint8Array(g.n), N = g.N;
  const plot = plotRing(doc);
  if (plot && plot.length >= 3) {
    for (let j = 0; j < N; j++) for (let i = 0; i < N; i++) cls[j * N + i] = pip(plot, cellMid(g, i), cellMid(g, j)) ? CLS_LAND : CLS_OUT;
  } else cls.fill(CLS_LAND);
  for (const [id, a] of Object.entries(doc.areas)) if (a.kind === 'lake') forCellsInPoly(g, areaRing(doc, id), k => { if (cls[k]) cls[k] = CLS_WATER; });
  for (const rv of Object.values(doc.rivers)) forCellsNearLine(g, riverPoly(rv.pts), () => rv.w / 2, k => { if (cls[k]) cls[k] = CLS_WATER; });
  for (const [id, a] of Object.entries(doc.areas)) {
    if (a.kind === 'lake' || a.kind === 'pool' || filling.has(id)) continue;
    const ring = areaRing(doc, id), mark = (k: number) => { if (cls[k]) cls[k] = CLS_RESERVED; if (isAnimalArea(a)) { hard[k] = 1; kept[k] = 1; } };
    forCellsInPoly(g, ring, mark);
    if (isAnimalArea(a) && ring.length >= 3) forCellsNearLine(g, ring.concat([ring[0] as Pt]), () => g.c * 0.75, mark);
  }
  for (const ic of Object.values(doc.icons)) {
    if (ic.type === 'entrance') continue;
    const r = ICON_CLEAR_M;
    forCellsNearLine(g, [[ic.x, ic.y], [ic.x, ic.y]], () => r, k => { if (cls[k] === CLS_LAND) cls[k] = CLS_RESERVED; });
  }
  for (const rl of roadLines(doc)) {
    forCellsNearLine(g, rl.poly, s => rl.hw(s) + g.c * ROAD_MARGIN_CELLS, k => { if (cls[k]) cls[k] = CLS_ROAD; });
    forCellsNearLine(g, rl.poly, rl.hw, k => { hard[k] = 1; });
  }
  return { g, cls, hard, kept };
}

const ROUTE_ORDER: Record<string, number> = { main: 0, cross: 1, path: 2 };

/** Roads in walking order (main loops, then cross paths, then other paths; entrance spurs are left out). */
export function walkingRoutes(doc: PlanDoc): Route[] {
  const roads = Object.entries(doc.roads).filter(([, r]) => r.kind !== 'entrance')
    .sort((a, b) => (ROUTE_ORDER[a[1].kind] ?? 2) - (ROUTE_ORDER[b[1].kind] ?? 2));
  return makeRoutes(roads.map(([, r]) => {
    const { pts, widths } = roadPoly(doc, r);
    return { poly: pts, w: widths.length ? Math.max(...widths) : 0 };
  }));
}

/**
 * Writes traced rings into the doc's corner pool: one new corner per grid corner used (shared between rings),
 * pinned at junctions. Returns the corner list per label.
 */
export function writeRings(doc: PlanDoc, g: Grid, tr: TraceResult, ids: IdMaker): Map<number, VertId[]> {
  const V = g.N + 1, byKey = new Map<number, VertId>(), out = new Map<number, VertId[]>();
  for (const [label, keys] of tr.rings) {
    out.set(label, keys.map(key => {
      let id = byKey.get(key);
      if (!id) {
        id = ids(doc.verts, 'v');
        const [x, y] = cornerToMetres(g, [key % V, Math.floor(key / V)]);
        doc.verts[id] = tr.junctions.has(key) ? { x, y, pinned: true } : { x, y };
        byKey.set(key, id);
      }
      return id;
    }));
  }
  return out;
}

/** Traces every label of `lab` with the shared-wall tracer (after absorbing holes) and writes the corners. */
export function traceInto(doc: PlanDoc, g: Grid, lab: Int32Array, hard: Uint8Array, ids: IdMaker): Map<number, VertId[]> {
  absorbHoles(g, lab);
  return writeRings(doc, g, traceRegions(lab, g.N, { tol: TRACE_TOL, hard }), ids);
}

/** Deletes the pools that belong to an area. */
export function deletePoolsOf(doc: PlanDoc, areaId: AreaId): void {
  for (const [id, a] of Object.entries(doc.areas)) if (a.kind === 'pool' && a.parent === areaId) delete doc.areas[id];
}

/**
 * Adds a pool inside a zone (cells labelled `label`) when its own water falls short of the need, grown from the
 * cell deepest inside the zone, with the prototype's 1.35× margin because smoothing trims it a little.
 */
export function addPool(doc: PlanDoc, c: Classified, lab: Int32Array, label: number, needWater: number, parent: AreaId, ids: IdMaker): void {
  const { g, cls } = c, { n, N } = g, cell2 = g.c * g.c, nb = new Int32Array(4);
  if (needWater <= 0) return;
  const mask = new Uint8Array(n), land: number[] = [];
  let have = 0;
  for (let k = 0; k < n; k++) if (lab[k] === label) { mask[k] = 1; if (cls[k] === CLS_WATER) have++; else if (cls[k] === CLS_LAND) land.push(k); }
  const need = Math.min(needWater * 1.35 / cell2 - have, land.length * 0.6);
  if (need <= 4 || !land.length) return;
  const d = new Int32Array(n).fill(-1), q: number[] = [];
  for (const k of land) {
    const m = nbrs4(g, k, nb);
    let edge = m < 4;
    for (let t = 0; t < m && !edge; t++) { const o = nb[t] as number; if (!mask[o] || cls[o] !== CLS_LAND) edge = true; }
    if (edge) { d[k] = 0; q.push(k); }
  }
  for (let a = 0; a < q.length; a++) {
    const k = q[a] as number, m = nbrs4(g, k, nb);
    for (let t = 0; t < m; t++) { const o = nb[t] as number; if (mask[o] && cls[o] === CLS_LAND && (d[o] as number) < 0) { d[o] = (d[k] as number) + 1; q.push(o); } }
  }
  let s0 = land[0] as number;
  for (const k of land) if ((d[k] as number) > (d[s0] as number)) s0 = k;
  const pm = new Uint8Array(n), pq = [s0];
  pm[s0] = 1;
  for (let a = 0; a < pq.length && pq.length < need; a++) {
    const m = nbrs4(g, pq[a] as number, nb);
    for (let t = 0; t < m && pq.length < need; t++) {
      const o = nb[t] as number;
      if (mask[o] && cls[o] === CLS_LAND && !pm[o]) { pm[o] = 1; pq.push(o); }
    }
  }
  const pts = simplifyLoop(traceMask(pm, N).map(p => cornerToMetres(g, p)), g.c * 1.2);
  if (pts.length < 3) return;
  const verts = pts.map(([x, y]) => { const id = ids(doc.verts, 'v'); doc.verts[id] = { x, y }; return id; });
  doc.areas[ids(doc.areas, 'a')] = { kind: 'pool', verts, parent };
}

/** One zone to place: a new one (no areaId) or an existing unlocked one being re-sliced. */
export interface ZoneItem {
  areaId?: AreaId;
  speciesId: SpeciesId;
  count: number;
  kind: AnimalAreaKind;
  /** Region (theme) index; regions are visited in the order of `groups`. */
  group: number;
  /** Needed area and water in m² (from speciesNeeds). */
  need: { area: number; water: number };
  /** Gets its frontage and its needed area before the other zones (the zone "Make room" is for). */
  priority?: boolean;
}

/**
 * Packs zones along the plan's roads and writes them into the doc (TD §7.2): existing zones get new corners,
 * new zones are added. Zones that don't fit get null and are left as they were. Pools of re-sliced zones are
 * replaced. Returns the area id per item.
 */
export function fillZones(doc: PlanDoc, items: ZoneItem[], groups: number[], waterGroup: number | null, r: Rng, ids: IdMaker): (AreaId | null)[] {
  const filling = new Set(items.flatMap(it => (it.areaId ? [it.areaId] : [])));
  const cl = classify(doc, filling), cell2 = cl.g.c * cl.g.c;
  const { lab } = packAlongRoutes({
    g: cl.g, cls: cl.cls, routes: walkingRoutes(doc), r, groups, waterGroup,
    items: items.map(it => ({ need: it.need.area / cell2, water: it.need.water * 1.15 / cell2, group: it.group, ...(it.priority ? { priority: true } : {}) })),
  });
  const rings = traceInto(doc, cl.g, lab, cl.hard, ids);
  return items.map((it, i) => {
    const ring = rings.get(i);
    if (!ring) return null;
    let id = it.areaId;
    const existing = id ? doc.areas[id] : undefined;
    if (id && existing && isAnimalArea(existing)) { existing.verts = ring; deletePoolsOf(doc, id); }
    else {
      id = ids(doc.areas, 'a');
      doc.areas[id] = { kind: it.kind, verts: ring, speciesId: it.speciesId, count: it.count, locked: false };
    }
    addPool(doc, cl, lab, i, it.need.water, id, ids);
    return id;
  });
}
